import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { CalendarAccess } from '@easyflow/shared';
import { canWriteCalendarObject } from '@/lib/calendar-access';
import type { CalendarSettingsRow } from '@easyflow/shared';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Sparkles,
  Calendar as CalendarIcon,
  List,
  LayoutGrid,
  Columns,
  Plus,
  Check,
  Ban,
  X,
  Pencil,
  Clock,
  AlignJustify,
  Rows,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Phone,
  Mail,
  CheckCircle2,
  AlertTriangle,
  Store,
  User,
} from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragEndEvent,
} from '@dnd-kit/core';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '@/lib/api';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@easyflow/ui-frontend/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useSocketOptional } from '@/contexts/SocketContext';
import { useMediaQuery } from '@/hooks/use-media-query';
import UserAvatar from '@/components/common/UserAvatar';
import AppointmentSourceBadge from '@/components/calendar/AppointmentSourceBadge';
import type { AppointmentSourceKind } from '@/lib/appointment-source-taxonomy';

type AttendanceState =
  | 'NOT_DUE' | 'DUE_SOON' | 'LATE' | 'ABSENT'
  | 'ACTIVE' | 'ON_BREAK' | 'EARLY_CLOCKOUT' | 'COMPLETED'
  | 'WALK_IN' | 'NO_SHIFT';

interface ShiftWindow {
  start_time: string;
  end_time: string;
  is_overnight: boolean;
}

interface AttendanceItem {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  snapshot: { state: AttendanceState; shift: ShiftWindow | null };
}

interface PlanAppointment {
  id: string;
  user_id: string;
  assigned_to_id?: string | null;
  title: string;
  start_time: string;
  end_time: string;
  status: string;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  customer_id?: string | null;
  lead_id?: string | null;
  person_id?: string | null;
  type_color?: string;
  type_name?: string;
  buffer_before?: number;
  buffer_after?: number;
  // Task #595: tassonomia unificata sorgente.
  source_kind?: AppointmentSourceKind;
}

interface CalendarUser {
  id: string;
  name: string;
  email: string;
  avatar_url?: string | null;
}

type CalendarSettings = Pick<CalendarSettingsRow,
  'slot_duration' | 'working_hours_start' | 'working_hours_end' | 'buffer_before' | 'buffer_after'>;

interface AppointmentTypeOption {
  id: string;
  name: string;
  color?: string;
  is_active?: boolean;
}

interface EligibilityCandidate {
  user_id: string;
  eligible?: boolean;
  score?: number;
  reasons?: string[];
}

interface SlotRanking {
  start: string;
  end: string;
  candidates: EligibilityCandidate[];
}

export interface AppointmentPlanningViewProps {
  access?: CalendarAccess | null;
  canManage?: boolean;
  selectedDate: Date;
  settings: CalendarSettings | null;
  calendarUsers: CalendarUser[];
  selectedStoreId: string;
  selectedUserId: string;
  serviceTypes?: AppointmentTypeOption[];
  // Task #595: filtro tassonomia sorgente, propagato dalla pagina
  // Calendar così che il multi-select del tab Calendar agisca anche
  // sul tab Planning (coerenza cross-view richiesta dal task).
  sourceKinds?: AppointmentSourceKind[];
  onCellClick: (userId: string, slotStart: Date, slotEnd: Date, serviceId?: string) => void;
  onAppointmentClick?: (id: string, appt: PlanAppointment) => void;
  onResourceClick?: (userId: string, userName: string) => void;
  onDateChange?: (date: Date) => void;
  /** Apre la scheda cliente person-first per l'appuntamento selezionato. */
  onOpenCustomerCard?: (appt: PlanAppointment) => void;
}

const STATE_DOT: Record<AttendanceState, string> = {
  ACTIVE: 'bg-emerald-500',
  WALK_IN: 'bg-teal-500',
  ON_BREAK: 'bg-amber-500',
  LATE: 'bg-orange-500',
  DUE_SOON: 'bg-yellow-500',
  NOT_DUE: 'bg-slate-400',
  ABSENT: 'bg-rose-500',
  EARLY_CLOCKOUT: 'bg-purple-500',
  COMPLETED: 'bg-gray-400',
  NO_SHIFT: 'bg-gray-300',
};

const STATE_LABEL: Record<AttendanceState, string> = {
  ACTIVE: 'Attivo',
  WALK_IN: 'Walk-in',
  ON_BREAK: 'In pausa',
  LATE: 'In ritardo',
  DUE_SOON: 'In arrivo',
  NOT_DUE: 'Non in turno',
  ABSENT: 'Assente',
  EARLY_CLOCKOUT: 'Uscito prima',
  COMPLETED: 'Turno terminato',
  NO_SHIFT: 'Nessun turno',
};

const STATUS_LABEL: Record<string, string> = {
  scheduled: 'Programmato',
  confirmed: 'Confermato',
  reminded: 'Promemoria',
  rescheduled: 'Riprogrammato',
  cancelled: 'Annullato',
  completed: 'Completato',
  no_show: 'Non presentato',
};

const STATUS_TONE: Record<string, string> = {
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
  reminded: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  rescheduled: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800',
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700',
  no_show: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800',
};

type Density = 'compact' | 'comfortable' | 'spacious';
const DENSITY_PX_PER_MIN: Record<Density, number> = { compact: 1.6, comfortable: 2.4, spacious: 3.2 };
const MIN_BLOCK_HEIGHT_PX = 36;
const DENSITY_KEY = 'planning.density';
const SIDEBAR_KEY = 'planning.sidebar';
const SNAP_MINUTES = 15;

// Time bands (Apple-style backgrounds): each band covers a portion of the day
// in minutes of the workday range; tone is applied via classes that work both
// in light and dark mode.
const TIME_BANDS = [
  { id: 'night',     label: 'Notte',      startMin: 0,            endMin: 5 * 60 + 30,  tone: 'bg-gray-100/50 dark:bg-gray-800/40' },
  { id: 'morning',   label: 'Mattina',    startMin: 6 * 60,       endMin: 13 * 60,      tone: 'bg-sky-50/60 dark:bg-sky-900/15' },
  { id: 'afternoon', label: 'Pomeriggio', startMin: 13 * 60 + 30, endMin: 18 * 60 + 30, tone: 'bg-amber-50/40 dark:bg-amber-900/10' },
  { id: 'evening',   label: 'Sera',       startMin: 19 * 60,      endMin: 23 * 60 + 30, tone: 'bg-indigo-50/50 dark:bg-indigo-900/15' },
] as const;

// Deterministic accent palette per user, used as left border of cards and as a
// secondary accent in the column header.
const USER_ACCENT_PALETTE = [
  '#ef4444', '#f97316', '#f59e0b', '#10b981', '#14b8a6',
  '#0ea5e9', '#6366f1', '#8b5cf6', '#d946ef', '#ec4899',
  '#84cc16', '#06b6d4',
];

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

function userAccentColor(id?: string | null): string {
  if (!id) return '#6366f1';
  return USER_ACCENT_PALETTE[hashStr(id) % USER_ACCENT_PALETTE.length]!;
}

// Lane assignment greedy: input list ordered by start; returns map id -> lane,
// and total number of lanes used by overlapping group of each appointment.
function computeLanes(list: PlanAppointment[]): Map<string, { lane: number; lanes: number }> {
  const sorted = [...list].sort((a, b) =>
    new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );
  const out = new Map<string, { lane: number; lanes: number }>();
  // Active lanes: array of end-time per lane index
  const lanes: number[] = [];
  // Track for each appt the lane group; we recompute lanes after assignment
  const assignment: { id: string; start: number; end: number; lane: number }[] = [];
  for (const a of sorted) {
    const s = new Date(a.start_time).getTime();
    const e = new Date(a.end_time).getTime();
    let lane = -1;
    for (let i = 0; i < lanes.length; i++) {
      if (lanes[i]! <= s) { lane = i; lanes[i] = e; break; }
    }
    if (lane < 0) { lane = lanes.length; lanes.push(e); }
    assignment.push({ id: a.id, start: s, end: e, lane });
  }
  // Group connected overlapping appointments to compute lanes count per group
  const sortedByStart = assignment.slice().sort((a, b) => a.start - b.start);
  let i = 0;
  while (i < sortedByStart.length) {
    let groupEnd = sortedByStart[i]!.end!;
    let maxLane = sortedByStart[i]!.lane!;
    let j = i + 1;
    while (j < sortedByStart.length && sortedByStart[j]!.start! < groupEnd) {
      if (sortedByStart[j]!.end! > groupEnd) groupEnd = sortedByStart[j]!.end!;
      if (sortedByStart[j]!.lane! > maxLane) maxLane = sortedByStart[j]!.lane!;
      j++;
    }
    const lanesCount = maxLane + 1;
    for (let k = i; k < j; k++) {
      out.set(sortedByStart[k]!.id!, { lane: sortedByStart[k]!.lane!, lanes: lanesCount });
    }
    i = j;
  }
  return out;
}

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function parseHM(v: string | undefined | null): { h: number; m: number } | null {
  if (!v) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(v);
  if (!m) return null;
  return { h: parseInt(m[1]!, 10), m: parseInt(m[2]!, 10) };
}

function dateAtTime(base: Date, t: { h: number; m: number }): Date {
  const d = new Date(base);
  d.setHours(t.h, t.m, 0, 0);
  return d;
}

function fmtTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

type ViewMode = 'day' | 'three_day' | 'week' | 'agenda';

export default function AppointmentPlanningView({
  access = null,
  canManage = false,
  selectedDate,
  settings,
  calendarUsers,
  selectedStoreId,
  selectedUserId,
  serviceTypes,
  sourceKinds,
  onCellClick,
  onAppointmentClick,
  onResourceClick,
  onDateChange,
  onOpenCustomerCard,
}: AppointmentPlanningViewProps) {
  const canEdit = useCallback(
    (appt: PlanAppointment) => canManage && canWriteCalendarObject(access, appt, 'edit'),
    [canManage, access],
  );
  const isMobile = useMediaQuery('(max-width: 639px)');
  const isTablet = useMediaQuery('(min-width: 640px) and (max-width: 1023px)');
  const isDesktop = !isMobile && !isTablet;
  const { toast } = useToast();

  const [view, setView] = useState<ViewMode>(() => (isMobile ? 'agenda' : isTablet ? 'three_day' : 'day'));
  const [attendance, setAttendance] = useState<AttendanceItem[]>([]);
  const [appointments, setAppointments] = useState<PlanAppointment[]>([]);
  const [weekAppointments, setWeekAppointments] = useState<PlanAppointment[]>([]);
  const [monthHeatmap, setMonthHeatmap] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [eligibility, setEligibility] = useState<{ slot_rankings: SlotRanking[] } | null>(null);
  const [mobileResourceId, setMobileResourceId] = useState<string>('');
  const [now, setNow] = useState<Date>(new Date());
  const [actionPending, setActionPending] = useState<string | null>(null);

  // Slot-picker popover (multi-resource day view): when admin clicks an empty
  // slot and there are multiple resources, we offer a chooser instead of
  // immediately creating for the clicked column user.
  const [slotPicker, setSlotPicker] = useState<{
    x: number;
    y: number;
    start: Date;
    end: Date;
    defaultUserId: string;
    candidates: { user_id: string; name: string; avatar_url?: string | null; busy: boolean }[];
  } | null>(null);

  const [density, setDensity] = useState<Density>(() => {
    if (typeof window === 'undefined') return 'comfortable';
    const v = localStorage.getItem(DENSITY_KEY);
    return v === 'compact' || v === 'spacious' ? v : 'comfortable';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem(DENSITY_KEY, density);
  }, [density]);

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const v = localStorage.getItem(SIDEBAR_KEY);
    return v == null ? true : v === '1';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem(SIDEBAR_KEY, sidebarOpen ? '1' : '0');
  }, [sidebarOpen]);

  const dateStr = toLocalDateStr(selectedDate);
  const startHM = useMemo(() => parseHM(settings?.working_hours_start) || { h: 8, m: 0 }, [settings?.working_hours_start]);
  const endHM = useMemo(() => parseHM(settings?.working_hours_end) || { h: 20, m: 0 }, [settings?.working_hours_end]);
  const workdayStartMin = startHM.h * 60 + startHM.m;
  const workdayEndMin = endHM.h * 60 + endHM.m;
  const totalDayMin = Math.max(60, workdayEndMin - workdayStartMin);
  const PX_PER_MIN = DENSITY_PX_PER_MIN[density];
  const colHeightPx = totalDayMin * PX_PER_MIN;
  const slotDuration = settings?.slot_duration || 30;

  // Reconcile view across breakpoints
  useEffect(() => {
    if (isMobile && view === 'three_day') setView('agenda');
    else if (isDesktop && view === 'three_day') setView('day');
  }, [isMobile, isDesktop, view]);

  // Now ticker: every 30s for animated now-line
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  // --- Day data
  const loadDay = useCallback(async () => {
    // Hard guard: never load planning data without a selected store. This
    // prevents accidentally rendering cross-store appointments and matches the
    // "sede obbligatoria" requirement.
    if (!selectedStoreId) {
      setAppointments([]);
      setAttendance([]);
      setLoading(false);
      setInitialLoading(false);
      return;
    }
    setLoading(true);
    try {
      const storeQS = `?store=${selectedStoreId}`;
      const [attResSettled, apResSettled] = await Promise.allSettled([
        apiClient.get<{ items: AttendanceItem[] }>(`/calendar/attendance-status?date=${dateStr}`),
        apiClient.get<PlanAppointment[]>(`/calendar/appointments/date/${dateStr}${storeQS}`),
      ]);
      if (attResSettled.status === 'fulfilled' && attResSettled.value.success && attResSettled.value.data) {
        setAttendance(attResSettled.value.data.items || []);
      } else {
        setAttendance([]);
        if (attResSettled.status === 'rejected') console.warn('attendance-status failed', attResSettled.reason);
      }
      if (apResSettled.status === 'fulfilled' && apResSettled.value.success) {
        setAppointments(apResSettled.value.data || []);
      } else if (apResSettled.status === 'rejected') {
        console.error('Failed to load appointments', apResSettled.reason);
      }
    } catch (e) {
      console.error('Planning load error', e);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [dateStr, selectedStoreId]);
  useEffect(() => { loadDay(); }, [loadDay]);

  // --- Week / 3-day data
  const weekDays = useMemo(() => {
    const out: Date[] = [];
    const ref = new Date(selectedDate);
    const dow = ref.getDay();
    const offset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(ref);
    monday.setDate(ref.getDate() + offset);
    monday.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      out.push(d);
    }
    return out;
  }, [selectedDate]);

  const threeDays = useMemo(() => {
    const out: Date[] = [];
    for (let i = 0; i < 3; i++) {
      const d = new Date(selectedDate);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + i);
      out.push(d);
    }
    return out;
  }, [selectedDate]);

  const rangeDays = view === 'three_day' ? threeDays : weekDays;

  const loadWeek = useCallback(async () => {
    if (!selectedStoreId) {
      setWeekAppointments([]);
      return;
    }
    const storeQS = `?store=${selectedStoreId}`;
    try {
      const results = await Promise.all(
        rangeDays.map((d) =>
          apiClient.get<PlanAppointment[]>(`/calendar/appointments/date/${toLocalDateStr(d)}${storeQS}`)
        )
      );
      const flat: PlanAppointment[] = [];
      for (const r of results) if (r.success && r.data) flat.push(...r.data);
      setWeekAppointments(flat);
    } catch (e) {
      console.error('Planning range load error', e);
    }
  }, [rangeDays, selectedStoreId]);
  useEffect(() => {
    if (view !== 'week' && view !== 'three_day') return;
    loadWeek();
  }, [view, loadWeek]);

  // --- Heatmap occupancy: load month around selectedDate (desktop sidebar)
  const monthDays = useMemo(() => {
    const out: Date[] = [];
    const first = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const last = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    for (let i = 0; i < last.getDate(); i++) {
      const d = new Date(first);
      d.setDate(first.getDate() + i);
      out.push(d);
    }
    return out;
  }, [selectedDate]);
  // Heatmap is keyed by month + store; we only refetch when that key changes
  // to avoid bursts of per-day requests on every appointment-list mutation.
  const heatmapKey = `${selectedDate.getFullYear()}-${selectedDate.getMonth()}|${selectedStoreId || ''}`;
  const lastHeatmapKeyRef = useRef<string>('');
  useEffect(() => {
    if (!isDesktop || !sidebarOpen) return;
    if (lastHeatmapKeyRef.current === heatmapKey) return;
    lastHeatmapKeyRef.current = heatmapKey;
    let cancelled = false;
    (async () => {
      try {
        const storeQS = selectedStoreId ? `?store=${selectedStoreId}` : '';
        const results = await Promise.all(
          monthDays.map((d) =>
            apiClient.get<PlanAppointment[]>(`/calendar/appointments/date/${toLocalDateStr(d)}${storeQS}`)
          )
        );
        if (cancelled) return;
        const m = new Map<string, number>();
        results.forEach((r, idx) => {
          const k = toLocalDateStr(monthDays[idx]!);
          const list = (r.success && r.data) ? r.data : [];
          const cnt = list.filter((a) => a.status !== 'cancelled').length;
          m.set(k, cnt);
        });
        setMonthHeatmap(m);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [isDesktop, sidebarOpen, heatmapKey, monthDays, selectedStoreId]);

  // Live updates
  const socketCtx = useSocketOptional();
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadDayRef = useRef(loadDay);
  const loadWeekRef = useRef(loadWeek);
  const viewRef = useRef(view);
  useEffect(() => { loadDayRef.current = loadDay; }, [loadDay]);
  useEffect(() => { loadWeekRef.current = loadWeek; }, [loadWeek]);
  useEffect(() => { viewRef.current = view; }, [view]);
  useEffect(() => {
    if (!socketCtx?.isConnected) return;
    const { on, off } = socketCtx;
    const schedule = () => {
      if (reloadTimerRef.current) return;
      reloadTimerRef.current = setTimeout(() => {
        reloadTimerRef.current = null;
        loadDayRef.current();
        if (viewRef.current === 'week' || viewRef.current === 'three_day') loadWeekRef.current();
      }, 400);
    };
    on('calendar:appointment_changed', schedule);
    on('hr:attendance_changed', schedule);
    return () => {
      off('calendar:appointment_changed', schedule);
      off('hr:attendance_changed', schedule);
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      reloadTimerRef.current = null;
    };
  }, [socketCtx, socketCtx?.isConnected, socketCtx?.socketVersion]);

  // Eligibility
  useEffect(() => {
    if (!selectedServiceId) { setEligibility(null); return; }
    const slots: { start: string; end: string }[] = [];
    let cur = dateAtTime(selectedDate, startHM);
    const limit = dateAtTime(selectedDate, endHM);
    while (cur < limit) {
      const next = new Date(cur.getTime() + slotDuration * 60_000);
      slots.push({ start: cur.toISOString(), end: next.toISOString() });
      cur = next;
    }
    if (slots.length === 0) return;
    let cancelled = false;
    apiClient
      .post<{ slot_rankings: SlotRanking[] }>('/calendar/eligible-resources/per-slot', {
        service_id: selectedServiceId,
        slots,
        store_id: selectedStoreId || null,
      })
      .then((r) => {
        if (cancelled) return;
        if (r.success && r.data) setEligibility({ slot_rankings: r.data.slot_rankings || [] });
        else setEligibility({ slot_rankings: [] });
      })
      .catch(() => { if (!cancelled) setEligibility({ slot_rankings: [] }); });
    return () => { cancelled = true; };
  }, [selectedServiceId, selectedStoreId, selectedDate, startHM.h, startHM.m, endHM.h, endHM.m, slotDuration, endHM, startHM]);

  const activeServices = useMemo(
    () => (serviceTypes || []).filter((t) => t.id && t.is_active !== false),
    [serviceTypes]
  );

  const resources = useMemo(() => {
    const attMap = new Map(attendance.map((a) => [a.user_id, a]));
    let users = calendarUsers
      .filter((u) => u.id)
      .map((u) => {
        const att = attMap.get(u.id);
        return {
          user_id: u.id,
          name: u.name || u.email,
          state: (att?.snapshot.state || 'NO_SHIFT'),
          shift: att?.snapshot.shift || null,
        };
      });
    if (selectedUserId && selectedUserId !== 'all') {
      users = users.filter((u) => u.user_id === selectedUserId);
    }
    users.sort((a, b) => a.name.localeCompare(b.name));
    return users;
  }, [attendance, calendarUsers, selectedUserId]);

  useEffect(() => {
    if (!isMobile) return;
    if (resources.length === 0) { setMobileResourceId(''); return; }
    if (!resources.find((r) => r.user_id === mobileResourceId)) setMobileResourceId(resources[0]!.user_id!);
  }, [isMobile, resources, mobileResourceId]);

  // Task #595: applica filtro tassonomia sorgente sull'array appuntamenti
  // PRIMA di derivare counters/timeline/agenda così che ogni vista (day,
  // three_day, week, agenda, contatori) rispetti il filtro propagato dal
  // tab Calendar. Lista vuota o undefined = nessun filtro (pass-through).
  // IMPORTANTE: deve essere dichiarato PRIMA di qualsiasi useMemo che lo
  // referenzi (apptsByResource, agendaList, counters) altrimenti TDZ.
  const filteredAppointments = useMemo(() => {
    if (!sourceKinds || sourceKinds.length === 0) return appointments;
    return appointments.filter((a) => (a.source_kind ? sourceKinds.includes(a.source_kind) : false));
  }, [appointments, sourceKinds]);

  const apptsByResource = useMemo(() => {
    const m = new Map<string, PlanAppointment[]>();
    // Task #595: usa filteredAppointments (rispetta multi-select sorgente).
    for (const a of filteredAppointments) {
      if (a.status === 'cancelled') continue;
      const uid = a.assigned_to_id || a.user_id;
      if (!uid) continue;
      const list = m.get(uid) || [];
      list.push(a);
      m.set(uid, list);
    }
    return m;
  }, [filteredAppointments]);

  // Live counters
  // NB: dipendenza su filteredAppointments (non su appointments) così
  // che i contatori reagiscano correttamente al cambio del filtro sorgente.
  const counters = useMemo(() => {
    const today = filteredAppointments.filter((a) => a.status !== 'cancelled');
    const toConfirm = today.filter((a) => a.status === 'scheduled').length;
    const inProgress = today.filter((a) => {
      const s = new Date(a.start_time);
      const e = new Date(a.end_time);
      return s <= now && now < e;
    }).length;
    // next free: first slot start time AFTER now where there is at least one resource with no overlapping appt
    let nextFree: Date | null = null;
    if (resources.length > 0) {
      let cur = new Date(selectedDate);
      cur.setHours(startHM.h, startHM.m, 0, 0);
      const nowAlignedFloor = new Date(now);
      nowAlignedFloor.setSeconds(0, 0);
      const start = cur > nowAlignedFloor ? cur : nowAlignedFloor;
      const end = new Date(selectedDate);
      end.setHours(endHM.h, endHM.m, 0, 0);
      const startMin = Math.ceil((start.getTime() - cur.getTime()) / 60000 / slotDuration) * slotDuration;
      cur = new Date(cur.getTime() + Math.max(0, startMin) * 60000);
      while (cur < end) {
        const slotEnd = new Date(cur.getTime() + slotDuration * 60000);
        const free = resources.some((r) => {
          const list = apptsByResource.get(r.user_id) || [];
          return !list.some((a) => {
            const s = new Date(a.start_time);
            const e = new Date(a.end_time);
            return s < slotEnd && e > cur;
          });
        });
        if (free) { nextFree = new Date(cur); break; }
        cur = slotEnd;
      }
    }
    return { total: today.length, toConfirm, inProgress, nextFree };
  }, [now, resources, apptsByResource, selectedDate, startHM, endHM, slotDuration, filteredAppointments]);

  function changeDay(deltaDays: number) {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + deltaDays);
    onDateChange?.(next);
  }

  // Swipe (mobile)
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  function handleTouchStart(e: React.TouchEvent) {
    const t = e.touches[0];
    touchStartXRef.current = t!.clientX!;
    touchStartYRef.current = t!.clientY!;
  }
  function handleTouchEnd(e: React.TouchEvent) {
    const sx = touchStartXRef.current;
    const sy = touchStartYRef.current;
    if (sx == null || sy == null) return;
    const t = e.changedTouches[0];
    const dx = t!.clientX! - sx;
    const dy = t!.clientY! - sy;
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.4) {
      // In mobile day view with multiple resources: horizontal swipe paginates
      // through resources (snap one at a time). Otherwise it changes day.
      if (view === 'day' && resources.length > 1) {
        const idx = Math.max(0, resources.findIndex((r) => r.user_id === mobileResourceId));
        const next = dx < 0
          ? Math.min(resources.length - 1, idx + 1)
          : Math.max(0, idx - 1);
        if (next !== idx) {
          setMobileResourceId(resources[next]!.user_id!);
          // brief haptic-style feedback toast
          toast({ title: resources[next]!.name!, description: `${next + 1} di ${resources.length}` });
          return;
        }
        // edge → fallback to day change
      }
      changeDay(dx < 0 ? 1 : -1);
    }
  }

  // Eligibility ranges per resource
  const bestPerSlotByUser = useMemo(() => {
    const m = new Map<string, Set<number>>();
    if (!selectedServiceId || !eligibility) return m;
    const visibleIds = new Set(resources.map((r) => r.user_id));
    eligibility.slot_rankings.forEach((rk, i) => {
      const eligible = rk.candidates.filter((c) => c.eligible && visibleIds.has(c.user_id));
      for (const cand of eligible) {
        const set = m.get(cand.user_id) || new Set<number>();
        set.add(i);
        m.set(cand.user_id, set);
      }
    });
    return m;
  }, [selectedServiceId, eligibility, resources]);

  const eligibleRangesByUser = useMemo(() => {
    const out = new Map<string, { topPx: number; heightPx: number }[]>();
    bestPerSlotByUser.forEach((set, uid) => {
      const sorted = Array.from(set).sort((a, b) => a - b);
      const ranges: { topPx: number; heightPx: number }[] = [];
      let runStart = -1;
      let prev = -2;
      for (const idx of sorted) {
        if (idx !== prev + 1) {
          if (runStart >= 0) {
            const len = prev - runStart + 1;
            const min = runStart * slotDuration;
            ranges.push({ topPx: min * PX_PER_MIN, heightPx: len * slotDuration * PX_PER_MIN });
          }
          runStart = idx;
        }
        prev = idx;
      }
      if (runStart >= 0) {
        const len = prev - runStart + 1;
        const min = runStart * slotDuration;
        ranges.push({ topPx: min * PX_PER_MIN, heightPx: len * slotDuration * PX_PER_MIN });
      }
      out.set(uid, ranges);
    });
    return out;
  }, [bestPerSlotByUser, slotDuration, PX_PER_MIN]);

  // Hour ticks
  const hourTicks = useMemo(() => {
    const out: { label: string; topPx: number }[] = [];
    for (let h = startHM.h; h <= endHM.h; h++) {
      const min = h * 60;
      const top = (min - workdayStartMin) * PX_PER_MIN;
      if (top < 0 || top > colHeightPx + 1) continue;
      out.push({ label: `${String(h).padStart(2, '0')}:00`, topPx: top });
    }
    return out;
  }, [startHM.h, endHM.h, workdayStartMin, colHeightPx, PX_PER_MIN]);

  const nowOffsetPx = useMemo(() => {
    const isToday = toLocalDateStr(now) === dateStr;
    if (!isToday) return null;
    const m = now.getHours() * 60 + now.getMinutes();
    if (m < workdayStartMin || m > workdayEndMin) return null;
    return (m - workdayStartMin) * PX_PER_MIN;
  }, [now, dateStr, workdayStartMin, workdayEndMin, PX_PER_MIN]);

  // === DnD setup ===
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 280, tolerance: 6 } }),
    useSensor(KeyboardSensor, {}),
  );

  // Snap delta minutes to grid
  const snapMin = useCallback((px: number) => {
    const min = px / PX_PER_MIN;
    return Math.round(min / SNAP_MINUTES) * SNAP_MINUTES;
  }, [PX_PER_MIN]);

  // PUT /calendar/appointments/:id with rollback toast
  const moveAppointment = useCallback(
    async (id: string, deltaMin: number, newUserId: string | null) => {
      if (!canManage) return;
      const orig = appointments.find((a) => a.id === id);
      if (!orig || !canEdit(orig)) return;
      if (newUserId && newUserId !== (orig.assigned_to_id || orig.user_id)
        && !canWriteCalendarObject(access, { assigned_to_id: newUserId }, 'edit')) return;
      const startTs = new Date(orig.start_time).getTime() + deltaMin * 60_000;
      const endTs = new Date(orig.end_time).getTime() + deltaMin * 60_000;
      const newStart = new Date(startTs).toISOString();
      const newEnd = new Date(endTs).toISOString();
      const userChanged = newUserId && newUserId !== (orig.assigned_to_id || orig.user_id);
      const patch: Record<string, unknown> = { start_time: newStart, end_time: newEnd };
      if (userChanged && newUserId) patch.assigned_to_id = newUserId;
      // optimistic
      setAppointments((prev) => prev.map((a) =>
        a.id === id ? { ...a, start_time: newStart, end_time: newEnd, ...(userChanged && newUserId ? { assigned_to_id: newUserId } : {}) } : a
      ));
      try {
        const res = await apiClient.put(`/calendar/appointments/${id}`, patch);
        if (!res.success) throw new Error(res.error || 'Aggiornamento fallito');
        toast({
          title: 'Appuntamento aggiornato',
          description: 'Trascinato in nuovo orario.',
          action: (
            <button
              type="button"
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={async () => {
                if (!canManage) return;
                // rollback
                setAppointments((prev) => prev.map((a) =>
                  a.id === id ? { ...a, start_time: orig.start_time, end_time: orig.end_time, assigned_to_id: orig.assigned_to_id ?? null } : a
                ));
                try {
                  await apiClient.put(`/calendar/appointments/${id}`, {
                    start_time: orig.start_time,
                    end_time: orig.end_time,
                    ...(userChanged ? { assigned_to_id: orig.assigned_to_id || orig.user_id } : {}),
                  });
                } catch { /* ignore */ }
              }}
            >
              Annulla
            </button>
          ),
        });
      } catch (e) {
        // rollback on error
        setAppointments((prev) => prev.map((a) =>
          a.id === id ? { ...a, start_time: orig.start_time, end_time: orig.end_time, assigned_to_id: orig.assigned_to_id ?? null } : a
        ));
        const msg = e instanceof Error ? e.message : 'Aggiornamento fallito';
        toast({ title: 'Errore', description: msg, variant: 'destructive' });
      }
    },
    [appointments, toast, canManage, access, canEdit]
  );

  const resizeAppointment = useCallback(
    async (id: string, deltaMin: number) => {
      if (!canManage) return;
      const orig = appointments.find((a) => a.id === id);
      if (!orig || !canEdit(orig)) return;
      const minDuration = settings?.slot_duration || 15;
      const origDur = (new Date(orig.end_time).getTime() - new Date(orig.start_time).getTime()) / 60_000;
      const newDur = Math.max(minDuration, origDur + deltaMin);
      const newEnd = new Date(new Date(orig.start_time).getTime() + newDur * 60_000).toISOString();
      setAppointments((prev) => prev.map((a) => a.id === id ? { ...a, end_time: newEnd } : a));
      try {
        const res = await apiClient.put(`/calendar/appointments/${id}`, { end_time: newEnd });
        if (!res.success) throw new Error(res.error || 'Aggiornamento fallito');
        toast({ title: 'Durata aggiornata', description: `${Math.round(newDur)} minuti` });
      } catch (e) {
        setAppointments((prev) => prev.map((a) => a.id === id ? { ...a, end_time: orig.end_time } : a));
        const msg = e instanceof Error ? e.message : 'Aggiornamento fallito';
        toast({ title: 'Errore', description: msg, variant: 'destructive' });
      }
    },
    [appointments, settings?.slot_duration, toast, canManage, access, canEdit]
  );

  function handleDragEnd(e: DragEndEvent) {
    if (!canManage) return;
    const id = String(e.active.id);
    const deltaMin = snapMin(e.delta.y);
    const overUserId = e.over?.id ? String(e.over.id) : null;
    if (deltaMin === 0 && (!overUserId || overUserId === (appointments.find(a => a.id === id)?.assigned_to_id || appointments.find(a => a.id === id)?.user_id))) {
      return;
    }
    moveAppointment(id, deltaMin, overUserId);
  }

  // Mobile resource visibility
  const visibleResources = useMemo(() => {
    if (isMobile) {
      const r = resources.find((x) => x.user_id === mobileResourceId);
      return r ? [r] : resources.slice(0, 1);
    }
    return resources;
  }, [resources, isMobile, mobileResourceId]);

  function handleColumnClick(uid: string, e: React.MouseEvent<HTMLDivElement>) {
    if (!canManage) return;
    if (!canWriteCalendarObject(access, { assigned_to_id: uid }, 'create')) return;
    const target = e.target as HTMLElement;
    if (target.closest('[data-appointment="1"]') || target.closest('[data-popover-trigger="1"]')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const minFromStart = Math.max(0, Math.round(y / PX_PER_MIN));
    const snapped = Math.floor(minFromStart / slotDuration) * slotDuration;
    const startMin = workdayStartMin + snapped;
    const start = new Date(selectedDate);
    start.setHours(0, 0, 0, 0);
    start.setMinutes(startMin);
    const end = new Date(start.getTime() + slotDuration * 60_000);

    // Multi-resource picker: when several consultants are visible (desktop day
    // view), show a popover with only FREE consultants for that slot. If only
    // one is free we skip the picker and create directly. If none are free we
    // fall back to creating on the clicked column (admins may want to override).
    if (!isMobile && view === 'day' && visibleResources.length >= 2) {
      const slotStartTs = start.getTime();
      const slotEndTs = end.getTime();
      const allCandidates = visibleResources.map((r) => {
        const list = apptsByResource.get(r.user_id) || [];
        const busy = list.some((a) => {
          if (a.status === 'cancelled') return false;
          const s = new Date(a.start_time).getTime();
          const eT = new Date(a.end_time).getTime();
          return s < slotEndTs && eT > slotStartTs;
        });
        const u = calendarUsers.find((x) => x.id === r.user_id);
        return { user_id: r.user_id, name: r.name, avatar_url: u?.avatar_url || null, busy };
      });
      const freeCandidates = allCandidates.filter((c) => !c.busy);
      // Direct-open shortcut if exactly one consultant is free.
      if (freeCandidates.length === 1) {
        onCellClick(freeCandidates[0]!.user_id!, start, end, selectedServiceId || undefined);
        return;
      }
      // If at least one is free, show the picker with only free entries.
      if (freeCandidates.length >= 2) {
        setSlotPicker({
          x: e.clientX, y: e.clientY, start, end, defaultUserId: uid,
          candidates: freeCandidates,
        });
        return;
      }
      // Nobody is free: fall back to clicked column (existing behavior).
    }

    onCellClick(uid, start, end, selectedServiceId || undefined);
  }

  // Quick action: change appointment status
  async function quickStatus(appt: PlanAppointment, next: string, label: string) {
    if (!canEdit(appt)) return;
    setActionPending(`${appt.id}:${next}`);
    try {
      const res = await apiClient.put(`/calendar/appointments/${appt.id}`, { status: next });
      if (!res.success) throw new Error(res.error || 'Aggiornamento fallito');
      // Optimistic update
      setAppointments((prev) => prev.map((a) => (a.id === appt.id ? { ...a, status: next } : a)));
      toast({ title: label });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Aggiornamento fallito';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setActionPending(null);
    }
  }

  const agendaList = useMemo(() => (
    // Task #595: usa filteredAppointments per coerenza col multi-select sorgente.
    filteredAppointments
      .filter((a) => a.status !== 'cancelled')
      .slice()
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
  ), [filteredAppointments]);

  // Week view aggregation
  const weekVisibleResources = useMemo(() => {
    if (selectedUserId && selectedUserId !== 'all') return resources.filter((r) => r.user_id === selectedUserId);
    return resources;
  }, [resources, selectedUserId]);
  // Task #595: filtro tassonomia sorgente applicato anche al dataset week
  // (usato da viste week / three_day) per coerenza con day/agenda.
  const filteredWeekAppointments = useMemo(() => {
    if (!sourceKinds || sourceKinds.length === 0) return weekAppointments;
    return weekAppointments.filter((a) => (a.source_kind ? sourceKinds.includes(a.source_kind) : false));
  }, [weekAppointments, sourceKinds]);

  const weekByResourceDay = useMemo(() => {
    const visibleIds = new Set(weekVisibleResources.map((r) => r.user_id));
    const allowedDays = new Set(rangeDays.map((d) => toLocalDateStr(d)));
    const out = new Map<string, Map<string, PlanAppointment[]>>();
    for (const a of filteredWeekAppointments) {
      if (a.status === 'cancelled') continue;
      const uid = a.assigned_to_id || a.user_id;
      if (!uid || !visibleIds.has(uid)) continue;
      const dayKey = toLocalDateStr(new Date(a.start_time));
      if (!allowedDays.has(dayKey)) continue;
      const perRes = out.get(uid) || new Map<string, PlanAppointment[]>();
      const list = perRes.get(dayKey) || [];
      list.push(a);
      perRes.set(dayKey, list);
      out.set(uid, perRes);
    }
    out.forEach((perRes) => perRes.forEach((v) => v.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())));
    return out;
  }, [filteredWeekAppointments, weekVisibleResources, rangeDays]);

  // === Renderers ===
  function ApptCard({
    a,
    onSelect,
    isConflict,
    overlapCount,
    userAccent,
  }: {
    a: PlanAppointment;
    onSelect?: () => void;
    isConflict?: boolean;
    overlapCount?: number;
    userAccent?: string;
  }) {
    const start = new Date(a.start_time);
    const end = new Date(a.end_time);
    const color = a.type_color || '#6366f1';
    const status = a.status;
    const tone = STATUS_TONE[status] || 'bg-gray-100 text-gray-700 border-gray-200';
    const pending = actionPending?.startsWith(`${a.id}:`);
    const durationMin = Math.round((end.getTime() - start.getTime()) / 60_000);
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-popover-trigger="1"
            onClick={(e) => { e.stopPropagation(); }}
            aria-label={`Appuntamento ${a.customer_name || a.title || ''} alle ${fmtTime(start)}, durata ${durationMin} minuti`}
            className={cn(
              'w-full h-full text-left rounded-md text-[11px] leading-tight px-2 py-1.5 overflow-hidden',
              'shadow-sm hover:shadow-md transition-all border bg-white dark:bg-gray-900',
              'hover:scale-[1.01] active:scale-[0.99]',
              status === 'cancelled' && 'opacity-60 line-through',
              status === 'no_show' && 'opacity-80',
              isConflict && 'border-amber-400 ring-1 ring-amber-300/60',
            )}
            style={{
              borderColor: isConflict ? '#fbbf24' : color + '55',
              borderLeft: `3px solid ${userAccent || color}`,
              backgroundImage: status === 'scheduled'
                ? `repeating-linear-gradient(135deg, transparent 0 8px, ${color}10 8px 9px)`
                : undefined,
            }}
            title={`${a.customer_name || a.title} · ${fmtTime(start)}–${fmtTime(end)}${isConflict && overlapCount && overlapCount > 1 ? ` · Conflitto: ${overlapCount} appuntamenti sovrapposti` : ''}`}
          >
            <div className="flex items-center gap-1 mb-0.5">
              {isConflict && <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />}
              <div className="font-semibold text-gray-900 dark:text-gray-100 truncate flex-1 min-w-0">
                {a.customer_name || a.title || 'Appuntamento'}
              </div>
              <Badge variant="outline" className={cn('text-[9px] px-1 py-0 h-4 border', tone)}>
                {STATUS_LABEL[status] || status}
              </Badge>
            </div>
            <div className="text-[10px] text-gray-600 dark:text-gray-400 truncate">
              {fmtTime(start)}–{fmtTime(end)}
              {a.type_name && <span> · {a.type_name}</span>}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-72 p-3 text-popover-foreground"
          align="start"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-2.5">
            <div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate flex-1 min-w-0">
                  {a.customer_name || a.title || 'Appuntamento'}
                </div>
                <Badge variant="outline" className={cn('text-[10px] border', tone)}>
                  {STATUS_LABEL[status] || status}
                </Badge>
                {a.source_kind && (
                  <AppointmentSourceBadge kind={a.source_kind} iconOnly className="text-[10px]" />
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {fmtTime(start)}–{fmtTime(end)}
                {a.type_name && <span>· {a.type_name}</span>}
              </div>
            </div>
            {(a.customer_phone || a.customer_email) && (
              <div className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5 border-t border-gray-200 dark:border-gray-700 pt-2">
                {a.customer_phone && (
                  <a href={`tel:${a.customer_phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 hover:underline">
                    <Phone className="w-3 h-3" /> {a.customer_phone}
                  </a>
                )}
                {a.customer_email && (
                  <a href={`mailto:${a.customer_email}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 hover:underline break-all">
                    <Mail className="w-3 h-3" /> {a.customer_email}
                  </a>
                )}
              </div>
            )}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">Azioni rapide</div>
              <div className="grid grid-cols-2 gap-1.5">
                <Button
                  size="sm" variant="outline" className="h-8 text-xs justify-start"
                  disabled={!canEdit(a) || status === 'confirmed' || pending}
                  onClick={() => quickStatus(a, 'confirmed', 'Appuntamento confermato')}
                >
                  {actionPending === `${a.id}:confirmed` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" />}
                  Conferma
                </Button>
                <Button
                  size="sm" variant="outline" className="h-8 text-xs justify-start"
                  disabled={!canEdit(a) || status === 'completed' || pending}
                  onClick={() => quickStatus(a, 'completed', 'Marcato come completato')}
                >
                  {actionPending === `${a.id}:completed` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                  Completato
                </Button>
                <Button
                  size="sm" variant="outline" className="h-8 text-xs justify-start"
                  disabled={!canEdit(a) || status === 'no_show' || pending}
                  onClick={() => quickStatus(a, 'no_show', 'Segnato come non presentato')}
                >
                  {actionPending === `${a.id}:no_show` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Ban className="w-3 h-3 mr-1" />}
                  Non presentato
                </Button>
                <Button
                  size="sm" variant="outline" className="h-8 text-xs justify-start text-rose-600 hover:text-rose-700"
                  disabled={!canEdit(a) || status === 'cancelled' || pending}
                  onClick={() => quickStatus(a, 'cancelled', 'Appuntamento annullato')}
                >
                  {actionPending === `${a.id}:cancelled` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <X className="w-3 h-3 mr-1" />}
                  Annulla
                </Button>
              </div>
              <Button
                size="sm" variant="ghost" className="w-full h-8 text-xs mt-1.5 justify-start hover:scale-100 active:scale-100"
                onClick={() => onSelect?.()}
              >
                <Pencil className="w-3 h-3 mr-1" /> Apri dettaglio
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  function renderColumn(res: typeof resources[number], extraStyle?: React.CSSProperties) {
    return (
      <ResourceColumn
        key={res.user_id}
        res={res}
        extraStyle={extraStyle}
      />
    );
  }

  // Inner component: one resource column. Uses useDroppable so DnD knows which
  // user the appointment is being dropped onto.
  function ResourceColumn({
    res,
    extraStyle,
  }: {
    res: typeof resources[number];
    extraStyle?: React.CSSProperties;
  }) {
    const { setNodeRef, isOver } = useDroppable({ id: res.user_id });
    const list = apptsByResource.get(res.user_id) || [];
    const isOnShift = res.shift !== null;
    const eligibleRanges = eligibleRangesByUser.get(res.user_id) || [];
    const lanesMap = computeLanes(list);

    // 3-level gradient grid (15/30/60 min lines) via CSS background.
    const min15 = SNAP_MINUTES * PX_PER_MIN;
    const min30 = 30 * PX_PER_MIN;
    const min60 = 60 * PX_PER_MIN;
    const gridBg: React.CSSProperties = {
      backgroundImage: [
        `repeating-linear-gradient(to bottom, rgba(229,231,235,0.55) 0 1px, transparent 1px ${min60}px)`,
        `repeating-linear-gradient(to bottom, rgba(229,231,235,0.35) 0 1px, transparent 1px ${min30}px)`,
        `repeating-linear-gradient(to bottom, rgba(229,231,235,0.18) 0 1px, transparent 1px ${min15}px)`,
      ].join(', '),
    };

    return (
      <div
        ref={setNodeRef}
        className={cn(
          'relative border-l border-gray-200 dark:border-gray-700 cursor-pointer group/col',
          isOver && 'bg-indigo-50/50 dark:bg-indigo-900/15',
        )}
        style={{ height: colHeightPx, ...extraStyle, ...gridBg }}
        onClick={(e) => handleColumnClick(res.user_id, e)}
      >
        {/* Time bands background (Notte/Mattina/Pomeriggio/Sera) */}
        {TIME_BANDS.map((b) => {
          const top = Math.max(0, (b.startMin - workdayStartMin) * PX_PER_MIN);
          const bottom = Math.min(colHeightPx, (b.endMin - workdayStartMin) * PX_PER_MIN);
          const height = bottom - top;
          if (height <= 0) return null;
          return (
            <div
              key={`band-${b.id}`}
              className={cn('absolute inset-x-0 pointer-events-none', b.tone)}
              style={{ top, height }}
            />
          );
        })}
        {/* Shift band overlay */}
        {isOnShift && res.shift && (() => {
          const s = parseHM(res.shift.start_time);
          const e = parseHM(res.shift.end_time);
          if (!s || !e) return null;
          const sMin = s.h * 60 + s.m;
          const eMin = e.h * 60 + e.m;
          const top = Math.max(0, (sMin - workdayStartMin) * PX_PER_MIN);
          const height = Math.max(0, Math.min(colHeightPx - top, (eMin - sMin) * PX_PER_MIN));
          return (
            <div
              className="absolute inset-x-0 bg-gradient-to-b from-emerald-50/40 to-emerald-50/10 dark:from-emerald-900/10 dark:to-emerald-900/5 pointer-events-none"
              style={{ top, height }}
            />
          );
        })()}
        {/* Hour grid (slightly stronger marker on each hour) */}
        {hourTicks.map((t, i) => (
          <div
            key={i}
            className="absolute inset-x-0 border-t border-gray-300/60 dark:border-gray-600/50 pointer-events-none"
            style={{ top: t.topPx }}
          />
        ))}
        {/* Free slot hover plus icon */}
        <div className="absolute inset-0 pointer-events-none opacity-0 group-hover/col:opacity-100 transition-opacity">
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] text-indigo-600 dark:text-indigo-300 bg-white/80 dark:bg-gray-900/80 backdrop-blur px-1.5 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-800">
            <Plus className="w-2.5 h-2.5" /> click per nuovo
          </div>
        </div>
        {/* Eligibility overlay */}
        {selectedServiceId && eligibleRanges.map((r, i) => (
          <div
            key={`elig-${i}`}
            className="absolute inset-x-1 rounded border border-dashed border-indigo-400/60 dark:border-indigo-500/50 bg-indigo-50/40 dark:bg-indigo-500/10 pointer-events-none flex items-start justify-end p-0.5"
            style={{ top: r.topPx, height: r.heightPx }}
          >
            <Sparkles className="w-3 h-3 text-indigo-500" />
          </div>
        ))}
        {/* Appointments (draggable + resizable) */}
        <AnimatePresence initial={false}>
          {list.map((a) => {
            const lane = lanesMap.get(a.id) || { lane: 0, lanes: 1 };
            return (
              <DraggableAppt
                key={a.id}
                a={a}
                lane={lane.lane}
                lanes={lane.lanes}
                userAccent={userAccentColor(a.assigned_to_id || a.user_id)}
                isConflict={lane.lanes > 1}
                overlapCount={lane.lanes}
              />
            );
          })}
        </AnimatePresence>
        {/* Now line animated */}
        {nowOffsetPx != null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="absolute inset-x-0 pointer-events-none z-10" style={{ top: nowOffsetPx }}
          >
            <div className="h-px bg-gradient-to-r from-transparent via-rose-500 to-transparent" />
            <div className="absolute -left-1 -top-1.5 w-3 h-3 rounded-full bg-rose-500 ring-2 ring-white dark:ring-gray-900 shadow shadow-rose-500/50">
              <span className="absolute inset-0 rounded-full bg-rose-400 animate-ping opacity-75" />
            </div>
          </motion.div>
        )}
      </div>
    );
  }

  // Wrapper that handles draggable + resize for one appointment.
  function DraggableAppt({
    a,
    lane,
    lanes,
    userAccent,
    isConflict,
    overlapCount,
  }: {
    a: PlanAppointment;
    lane: number;
    lanes: number;
    userAccent: string;
    isConflict: boolean;
    overlapCount: number;
  }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: a.id, disabled: !canEdit(a) });
    const resizingRef = useRef<{ startY: number; startHeightPx: number } | null>(null);
    const [liveResizePx, setLiveResizePx] = useState(0);

    const start = new Date(a.start_time);
    const end = new Date(a.end_time);
    const sMin = start.getHours() * 60 + start.getMinutes();
    const eMin = end.getHours() * 60 + end.getMinutes();
    if (eMin <= workdayStartMin || sMin >= workdayEndMin) return null;
    const top = Math.max(0, (sMin - workdayStartMin) * PX_PER_MIN);
    const baseHeight = Math.max(28, ((Math.min(eMin, workdayEndMin) - Math.max(sMin, workdayStartMin)) * PX_PER_MIN));
    const widthPct = 100 / Math.max(1, lanes);
    const leftPct = lane * widthPct;
    const bb = a.buffer_before || settings?.buffer_before || 0;
    const ba = a.buffer_after || settings?.buffer_after || 0;
    const color = a.type_color || '#6366f1';

    const dragging = !!transform || !!isDragging;
    const dragDeltaY = transform?.y ?? 0;
    // Snapped delta is only used for the floating "preview time" badge so the
    // user sees what the drop orario will be. The *actual* block position
    // during drag follows the raw pointer delta via CSS transform — snapping
    // the visual position to slot boundaries on every frame made the block
    // look like it was vibrating because micro re-renders of the parent
    // would re-trigger the motion layout.
    const snappedDeltaY = Math.round(dragDeltaY / (SNAP_MINUTES * PX_PER_MIN)) * (SNAP_MINUTES * PX_PER_MIN);

    // Resize state via pointer events
    const onResizeStart = (e: React.PointerEvent) => {
      if (!canEdit(a)) return;
      e.stopPropagation();
      e.preventDefault();
      (e.target as Element).setPointerCapture(e.pointerId);
      resizingRef.current = { startY: e.clientY, startHeightPx: baseHeight };
      setLiveResizePx(0);
    };
    const onResizeMove = (e: React.PointerEvent) => {
      if (!resizingRef.current) return;
      const dy = e.clientY - resizingRef.current.startY;
      const snapped = Math.round(dy / (SNAP_MINUTES * PX_PER_MIN)) * (SNAP_MINUTES * PX_PER_MIN);
      setLiveResizePx(snapped);
    };
    const onResizeEnd = (e: React.PointerEvent) => {
      if (!resizingRef.current) return;
      const dy = e.clientY - resizingRef.current.startY;
      const snapped = Math.round(dy / (SNAP_MINUTES * PX_PER_MIN)) * (SNAP_MINUTES * PX_PER_MIN);
      const deltaMin = snapped / PX_PER_MIN;
      resizingRef.current = null;
      setLiveResizePx(0);
      if (Math.abs(deltaMin) >= SNAP_MINUTES) {
        resizeAppointment(a.id, deltaMin);
      }
    };

    const liveHeight = Math.max(MIN_BLOCK_HEIGHT_PX, baseHeight + liveResizePx);
    // While dragging the block stays at its original `top` and we translate it
    // with a GPU-composited CSS transform using the raw pointer delta. This
    // prevents layout thrash / jitter whenever the parent re-renders (socket
    // refreshes, conflict recomputation, hover highlight changes, etc.).
    const liveTop = top;
    const previewMinutes = Math.round((liveHeight) / PX_PER_MIN);
    const showResizeBadge = liveResizePx !== 0;

    return (
      <div
        data-appointment="1"
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        className={cn('absolute', dragging && 'z-30', 'select-none')}
        style={{
          top: liveTop,
          height: liveHeight,
          left: `calc(${leftPct}% + 4px)`,
          width: `calc(${widthPct}% - 8px)`,
          touchAction: 'none',
          // No CSS `transition` during drag so the block follows the pointer
          // exactly, frame by frame — no spring, no interpolation, no jitter.
          transition: dragging ? 'none' : 'box-shadow 120ms ease',
          willChange: dragging ? 'transform' : undefined,
          transform: dragging
            ? `translate3d(0, ${dragDeltaY}px, 0) rotate(-1.2deg)`
            : undefined,
          boxShadow: dragging ? '0 12px 24px rgba(0,0,0,0.18)' : undefined,
          cursor: dragging ? 'grabbing' : 'grab',
        }}
      >
        {bb > 0 && (
          <div
            className="absolute inset-x-0 -top-[var(--bb)] rounded pointer-events-none"
            style={{
              ['--bb' as never]: `${bb * PX_PER_MIN}px`,
              top: -bb * PX_PER_MIN,
              height: bb * PX_PER_MIN,
              backgroundImage: `repeating-linear-gradient(45deg, transparent 0 4px, ${color}22 4px 5px)`,
            }}
          />
        )}
        {ba > 0 && (
          <div
            className="absolute inset-x-0 rounded pointer-events-none"
            style={{
              top: liveHeight,
              height: ba * PX_PER_MIN,
              backgroundImage: `repeating-linear-gradient(45deg, transparent 0 4px, ${color}22 4px 5px)`,
            }}
          />
        )}
        <ApptCard
          a={a}
          onSelect={() => onAppointmentClick?.(a.id, a)}
          isConflict={isConflict}
          userAccent={userAccent}
          overlapCount={overlapCount}
        />
        {/* Resize handle */}
        {canEdit(a) && <div
          onPointerDown={onResizeStart}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeEnd}
          onPointerCancel={onResizeEnd}
          onClick={(e) => e.stopPropagation()}
          className="absolute left-1/2 -translate-x-1/2 bottom-0 w-12 h-2 rounded-t bg-transparent hover:bg-indigo-200/60 dark:hover:bg-indigo-500/40 cursor-ns-resize transition-colors"
          style={{ touchAction: 'none' }}
          aria-label="Ridimensiona appuntamento"
          role="separator"
        />}
        {(showResizeBadge || (dragging && snappedDeltaY !== 0)) && (
          <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] px-1.5 py-0.5 rounded bg-gray-900 text-white shadow z-40 whitespace-nowrap">
            {showResizeBadge
              ? `${Math.floor(previewMinutes / 60)}h ${previewMinutes % 60}m`
              : (() => {
                  const newStart = new Date(start.getTime() + (snappedDeltaY / PX_PER_MIN) * 60_000);
                  return fmtTime(newStart);
                })()}
          </div>
        )}
      </div>
    );
  }

  // === MAIN LAYOUT ===
  // Viewport-fixed shell: external page should not scroll. Use 100dvh minus
  // approximate page chrome (top bar + tab strip = ~10rem on desktop).
  const shellHeight = isMobile ? 'calc(100dvh - 8rem)' : 'calc(100dvh - 10rem)';

  const showSidebar = isDesktop && sidebarOpen;
  const heatmapMax = useMemo(() => {
    let max = 0;
    monthHeatmap.forEach((v) => { if (v > max) max = v; });
    return max || 1;
  }, [monthHeatmap]);

  return (
    <div
      className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm flex flex-col overflow-hidden"
      style={{ height: shellHeight, minHeight: 480 }}
    >
      {/* TOOLBAR */}
      <div className="shrink-0 px-3 sm:px-4 py-2.5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-white to-gray-50/50 dark:from-gray-900 dark:to-gray-900/80">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Left: date nav */}
          <div className="flex items-center gap-1.5 min-w-0">
            {isDesktop && (
              <Button
                variant="ghost" size="icon" className="h-9 w-9"
                onClick={() => setSidebarOpen((v) => !v)}
                aria-label={sidebarOpen ? 'Chiudi pannello' : 'Apri pannello'}
                title={sidebarOpen ? 'Chiudi pannello' : 'Apri pannello'}
              >
                {sidebarOpen ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => changeDay(-1)} aria-label="Giorno precedente">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex flex-col min-w-0">
              <div className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white whitespace-nowrap truncate">
                {selectedDate.toLocaleDateString('it-IT', { weekday: isMobile ? 'short' : 'long', day: 'numeric', month: isMobile ? 'short' : 'long' })}
              </div>
              {visibleResources.length === 1 && (
                <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate" title={visibleResources[0]!.name!}>
                  {visibleResources[0]!.name!}
                </div>
              )}
            </div>
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => changeDay(1)} aria-label="Giorno successivo">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" className="h-9 hidden sm:inline-flex" onClick={() => onDateChange?.(new Date())}>
              Oggi
            </Button>
          </div>
          {/* Right: actions */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {activeServices.length > 0 && (
              <select
                value={selectedServiceId}
                onChange={(e) => setSelectedServiceId(e.target.value)}
                className="text-xs px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 h-9 max-w-[160px]"
                title="Filtra per servizio"
              >
                <option value="">Tutti i servizi</option>
                {activeServices.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
            )}
            <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-700 overflow-hidden h-9">
              <button
                className={cn('px-2.5 text-xs inline-flex items-center gap-1 transition-colors', view === 'day' ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800')}
                onClick={() => setView('day')}
                title="Giorno"
              >
                <CalendarIcon className="w-3.5 h-3.5" /><span className="hidden sm:inline">Giorno</span>
              </button>
              {isTablet && (
                <button
                  className={cn('px-2.5 text-xs inline-flex items-center gap-1 border-l border-gray-300 dark:border-gray-700 transition-colors', view === 'three_day' ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800')}
                  onClick={() => setView('three_day')}
                  title="3 giorni"
                >
                  <Columns className="w-3.5 h-3.5" /><span className="hidden sm:inline">3 gg</span>
                </button>
              )}
              <button
                className={cn('px-2.5 text-xs inline-flex items-center gap-1 border-l border-gray-300 dark:border-gray-700 transition-colors', view === 'week' ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800')}
                onClick={() => setView('week')}
                title="Settimana"
              >
                <LayoutGrid className="w-3.5 h-3.5" /><span className="hidden sm:inline">Sett.</span>
              </button>
              <button
                className={cn('px-2.5 text-xs inline-flex items-center gap-1 border-l border-gray-300 dark:border-gray-700 transition-colors', view === 'agenda' ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800')}
                onClick={() => setView('agenda')}
                title="Agenda"
              >
                <List className="w-3.5 h-3.5" /><span className="hidden sm:inline">Agenda</span>
              </button>
            </div>
            {!isMobile && view === 'day' && (
              <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-700 overflow-hidden h-9" title="Densità">
                <button
                  className={cn('px-2 inline-flex items-center transition-colors', density === 'compact' ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800')}
                  onClick={() => setDensity('compact')}
                  aria-label="Vista compatta"
                  title="Vista compatta"
                >
                  <Menu className="w-3.5 h-3.5" />
                </button>
                <button
                  className={cn('px-2 inline-flex items-center border-l border-gray-300 dark:border-gray-700 transition-colors', density === 'comfortable' ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800')}
                  onClick={() => setDensity('comfortable')}
                  aria-label="Comfortable"
                  title="Comfortable"
                >
                  <Rows className="w-3.5 h-3.5" />
                </button>
                <button
                  className={cn('px-2 inline-flex items-center border-l border-gray-300 dark:border-gray-700 transition-colors', density === 'spacious' ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800')}
                  onClick={() => setDensity('spacious')}
                  aria-label="Spacious"
                  title="Spacious"
                >
                  <AlignJustify className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <Button
              variant="ghost" size="icon" className="h-9 w-9"
              onClick={() => { loadDay(); if (view === 'week' || view === 'three_day') loadWeek(); }}
              disabled={loading}
              aria-label="Aggiorna"
              title="Aggiorna"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        {/* Live counters */}
        <div className="mt-2 flex items-center gap-1.5 sm:gap-2 flex-wrap text-[11px]">
          <Counter label="Oggi" value={counters.total} tone="indigo" />
          <Counter label="Da confermare" value={counters.toConfirm} tone="amber" pulse={counters.toConfirm > 0} />
          <Counter label="In corso" value={counters.inProgress} tone="emerald" pulse={counters.inProgress > 0} />
          {counters.nextFree && (
            <Counter label="Prossimo libero" value={fmtTime(counters.nextFree)} tone="slate" />
          )}
        </div>
        {isMobile && view === 'day' && resources.length > 1 && (() => {
          const idx = Math.max(0, resources.findIndex((r) => r.user_id === mobileResourceId));
          const active = resources[idx];
          const u = calendarUsers.find((x) => x.id === active?.user_id);
          return (
            <div className="mt-2 flex flex-col items-center gap-1.5">
              <div className="flex items-center gap-2">
                <UserAvatar
                  id={active!.user_id!}
                  name={active!.name!}
                  email={u?.email}
                  avatarUrl={u?.avatar_url || undefined}
                  size="sm"
                />
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {active!.name!}
                </div>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                  {idx + 1} / {resources.length}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {resources.map((r, i) => (
                  <button
                    key={r.user_id}
                    aria-label={`Vai a ${r.name}`}
                    aria-current={i === idx ? 'true' : undefined}
                    onClick={() => setMobileResourceId(r.user_id)}
                    className="inline-flex items-center justify-center w-9 h-9 -m-2"
                  >
                    <span
                      className={cn(
                        'block rounded-full transition-all',
                        i === idx
                          ? 'w-4 h-1.5 bg-indigo-600 dark:bg-indigo-400'
                          : 'w-1.5 h-1.5 bg-gray-300 dark:bg-gray-600'
                      )}
                    />
                  </button>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* BODY: optional sidebar + grid */}
      <div className="flex-1 min-h-0 flex">
        {showSidebar && (
          <div className="shrink-0 w-44 lg:w-56 border-r border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 overflow-y-auto p-2 lg:p-3 space-y-3">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  {selectedDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                </div>
                <div className="flex gap-0.5">
                  <button
                    onClick={() => {
                      const d = new Date(selectedDate);
                      d.setMonth(d.getMonth() - 1);
                      onDateChange?.(d);
                    }}
                    className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-800"
                    aria-label="Mese precedente"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => {
                      const d = new Date(selectedDate);
                      d.setMonth(d.getMonth() + 1);
                      onDateChange?.(d);
                    }}
                    className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-800"
                    aria-label="Mese successivo"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <MiniCalendar
                selectedDate={selectedDate}
                onSelect={(d) => onDateChange?.(d)}
                heatmap={monthHeatmap}
                heatmapMax={heatmapMax}
              />
              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                <span>Occupazione</span>
                <span className="inline-block w-3 h-2 rounded-sm bg-indigo-100 dark:bg-indigo-900/30" />
                <span className="inline-block w-3 h-2 rounded-sm bg-indigo-300 dark:bg-indigo-700" />
                <span className="inline-block w-3 h-2 rounded-sm bg-indigo-500" />
                <span className="inline-block w-3 h-2 rounded-sm bg-indigo-700" />
              </div>
            </div>
            {/* Resource summary */}
            <div className="border-t border-gray-200 dark:border-gray-800 pt-3">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Risorse ({resources.length})
              </div>
              <div className="space-y-1">
                {resources.slice(0, 12).map((r) => (
                  <button
                    key={r.user_id}
                    onClick={() => onResourceClick?.(r.user_id, r.name)}
                    className="w-full flex items-center gap-1.5 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-left"
                  >
                    <div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-[9px] font-semibold shrink-0">
                      {initials(r.name)}
                    </div>
                    <div className="flex-1 min-w-0 text-[11px] text-gray-800 dark:text-gray-200 truncate">{r.name}</div>
                    <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', STATE_DOT[r.state])} title={STATE_LABEL[r.state]} />
                  </button>
                ))}
                {resources.length > 12 && (
                  <div className="text-[10px] text-gray-500 px-1.5">+{resources.length - 12} altri</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Grid area scrolls; the page never does */}
        <div className="flex-1 min-w-0 overflow-hidden flex flex-col">
          {initialLoading ? (
            <SkeletonGrid />
          ) : view === 'agenda' ? (
            <div
              className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-1.5"
              onTouchStart={isMobile ? handleTouchStart : undefined}
              onTouchEnd={isMobile ? handleTouchEnd : undefined}
            >
              {agendaList.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nessun appuntamento per questa giornata.
                </div>
              ) : (
                agendaList.map((a) => {
                  const start = new Date(a.start_time);
                  const end = new Date(a.end_time);
                  const color = a.type_color || '#6366f1';
                  const assignedName = calendarUsers.find((u) => u.id === (a.assigned_to_id || a.user_id))?.name || '';
                  const status = a.status;
                  const hasContact = !!(a.person_id || a.customer_id || a.lead_id);
                  return (
                    <div
                      key={a.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => onAppointmentClick?.(a.id, a)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAppointmentClick?.(a.id, a); } }}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-sm transition-all min-h-[60px] flex items-center gap-3 bg-white dark:bg-gray-900 cursor-pointer"
                      style={{ borderLeftWidth: 4, borderLeftColor: color }}
                    >
                      <div className="text-sm font-mono text-gray-700 dark:text-gray-300 w-20 shrink-0">
                        <div className="font-semibold">{fmtTime(start)}</div>
                        <div className="text-[10px] text-gray-500">{fmtTime(end)}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate flex items-center gap-2">
                          <span className="truncate">{a.customer_name || a.title || 'Appuntamento'}</span>
                          <Badge variant="outline" className={cn('text-[10px] border', STATUS_TONE[status] || '')}>
                            {STATUS_LABEL[status] || status}
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {a.type_name ? a.type_name : 'Appuntamento'}
                          {assignedName ? ` · ${assignedName}` : ''}
                        </div>
                      </div>
                      {hasContact && onOpenCustomerCard && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onOpenCustomerCard(a); }}
                          className="shrink-0 p-1.5 rounded-md text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950"
                          title="Apri scheda cliente"
                          aria-label="Apri scheda cliente"
                        >
                          <User className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          ) : view === 'week' || view === 'three_day' ? (
            weekVisibleResources.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                Nessuna risorsa selezionata.
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-auto px-3 py-3">
                <div
                  className="grid gap-1"
                  style={{
                    gridTemplateColumns: `160px repeat(${rangeDays.length}, minmax(${view === 'three_day' ? '180px' : '110px'}, 1fr))`,
                    minWidth: view === 'three_day' ? 160 + rangeDays.length * 180 : 800,
                  }}
                >
                  <div className="sticky top-0 left-0 z-20 bg-white dark:bg-gray-900" />
                  {rangeDays.map((d) => {
                    const k = toLocalDateStr(d);
                    const isToday = toLocalDateStr(new Date()) === k;
                    const isSelected = k === dateStr;
                    return (
                      <button
                        key={`hd-${k}`}
                        onClick={() => onDateChange?.(d)}
                        className={cn(
                          'sticky top-0 z-10 text-xs px-2 py-1.5 rounded text-left bg-white dark:bg-gray-900',
                          isSelected ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200' :
                          isToday ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200' :
                          'text-gray-700 dark:text-gray-300'
                        )}
                      >
                        <div className="font-semibold">{d.toLocaleDateString('it-IT', { weekday: 'short' })}</div>
                        <div className="text-[10px]">{d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}</div>
                      </button>
                    );
                  })}
                  {weekVisibleResources.map((res) => (
                    <React.Fragment key={`row-${res.user_id}`}>
                      <div
                        className="px-2 py-1.5 flex items-center gap-2 cursor-pointer sticky left-0 bg-white dark:bg-gray-900 z-10 border-r border-gray-200 dark:border-gray-800"
                        onClick={() => onResourceClick?.(res.user_id, res.name)}
                      >
                        <div className="w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-[10px] font-semibold shrink-0">
                          {initials(res.name)}
                        </div>
                        <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">{res.name}</div>
                        <span className={cn('w-2 h-2 rounded-full shrink-0 ml-auto', STATE_DOT[res.state])} />
                      </div>
                      {rangeDays.map((d) => {
                        const k = toLocalDateStr(d);
                        const list = weekByResourceDay.get(res.user_id)?.get(k) || [];
                        return (
                          <div
                            key={`c-${res.user_id}-${k}`}
                            className="min-h-[80px] p-1 space-y-1 border border-dashed border-gray-200 dark:border-gray-700 rounded"
                          >
                            {list.length === 0 ? (
                              <div className="text-[10px] text-gray-300 dark:text-gray-600 text-center pt-1">—</div>
                            ) : (
                              list.map((a) => {
                                const color = a.type_color || '#6366f1';
                                const start = new Date(a.start_time);
                                return (
                                  <button
                                    key={a.id}
                                    onClick={() => onAppointmentClick?.(a.id, a)}
                                    className="block w-full text-left p-1.5 rounded text-[11px] border hover:shadow-sm transition-shadow"
                                    style={{ backgroundColor: color + '22', borderColor: color + '55', borderLeft: `3px solid ${color}` }}
                                  >
                                    <div className="font-mono text-[10px] text-gray-700 dark:text-gray-300">{fmtTime(start)}</div>
                                    <div className="truncate text-gray-900 dark:text-gray-100">
                                      {a.customer_name || a.title || '—'}
                                    </div>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )
          ) : (
            // DAY view: unico contenitore scrollabile, wrappato in DndContext per drag&drop.
            <DndContext
              sensors={sensors}
              onDragEnd={handleDragEnd}
            >
              {(() => {
                const mobileMulti = isMobile && resources.length > 1;
                const mobileIdx = mobileMulti
                  ? Math.max(0, resources.findIndex((r) => r.user_id === mobileResourceId))
                  : 0;
                const goTo = (next: number) => {
                  const clamped = Math.max(0, Math.min(resources.length - 1, next));
                  if (clamped !== mobileIdx) setMobileResourceId(resources[clamped]!.user_id!);
                };
                const innerJsx = visibleResources.length === 0 ? (
                  <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                    Nessuna risorsa disponibile.
                  </div>
                ) : (
                  <div
                    className="grid"
                    style={{
                      gridTemplateColumns: `64px repeat(${visibleResources.length}, minmax(${isMobile ? 'calc(100% - 64px)' : '200px'}, 1fr))`,
                    }}
                  >
                    {/* Angolo top-left (sticky in entrambe le direzioni) */}
                    <div className="sticky top-0 left-0 z-30 h-12 bg-white dark:bg-gray-900 border-b border-r border-gray-200 dark:border-gray-700" />
                    {/* Header risorse sticky-top con avatar foto + accent color */}
                    {visibleResources.map((res) => {
                      const accent = userAccentColor(res.user_id);
                      const u = calendarUsers.find((x) => x.id === res.user_id);
                      return (
                        <div
                          key={`h-${res.user_id}`}
                          className="h-12 px-2 py-1.5 sticky top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-l border-gray-200 dark:border-gray-700 z-20 cursor-pointer flex items-center gap-2"
                          style={{ boxShadow: `inset 0 -2px 0 0 ${accent}` }}
                          onClick={() => onResourceClick?.(res.user_id, res.name)}
                        >
                          <UserAvatar
                            id={res.user_id}
                            name={res.name}
                            email={u?.email}
                            avatarUrl={u?.avatar_url || undefined}
                            size="sm"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">{res.name}</div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{STATE_LABEL[res.state]}</div>
                          </div>
                          <span className={cn('w-2 h-2 rounded-full shrink-0 ring-2 ring-white dark:ring-gray-900', STATE_DOT[res.state])} title={STATE_LABEL[res.state]} />
                        </div>
                      );
                    })}
                    {/* Righello orari sticky-left con etichette bande verticali */}
                    <div
                      className="sticky left-0 z-10 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800"
                      style={{ height: colHeightPx }}
                    >
                      {/* Time band vertical labels */}
                      {TIME_BANDS.map((b) => {
                        const top = Math.max(0, (b.startMin - workdayStartMin) * PX_PER_MIN);
                        const bottom = Math.min(colHeightPx, (b.endMin - workdayStartMin) * PX_PER_MIN);
                        const height = bottom - top;
                        if (height <= 24) return null;
                        return (
                          <div
                            key={`bl-${b.id}`}
                            className="absolute left-0 w-4 flex items-center justify-center text-[9px] font-semibold tracking-wide text-gray-400 dark:text-gray-500 pointer-events-none uppercase"
                            style={{
                              top,
                              height,
                              writingMode: 'vertical-rl',
                              transform: 'rotate(180deg)',
                            }}
                          >
                            {b.label}
                          </div>
                        );
                      })}
                      {hourTicks.map((t, i) => {
                        const isFirst = i === 0;
                        return (
                          <div
                            key={i}
                            className={cn(
                              'absolute right-2 text-[10px] font-medium text-gray-500 dark:text-gray-400',
                              !isFirst && '-translate-y-1/2'
                            )}
                            style={{ top: isFirst ? Math.max(2, t.topPx) : t.topPx }}
                          >
                            {t.label}
                          </div>
                        );
                      })}
                    </div>
                    {/* Colonne risorse */}
                    {visibleResources.map((res) => renderColumn(res))}
                  </div>
                );
                if (mobileMulti) {
                  return (
                    <motion.div
                      className="flex-1 min-h-0 overflow-auto"
                      drag="x"
                      dragConstraints={{ left: 0, right: 0 }}
                      dragElastic={0.18}
                      style={{ touchAction: 'pan-y' }}
                      onDragEnd={(_e, info) => {
                        const dx = info.offset.x;
                        const vx = info.velocity.x;
                        if (dx < -80 || vx < -500) goTo(mobileIdx + 1);
                        else if (dx > 80 || vx > 500) goTo(mobileIdx - 1);
                      }}
                    >
                      {innerJsx}
                    </motion.div>
                  );
                }
                return (
                  <div
                    className="flex-1 min-h-0 overflow-auto"
                    onTouchStart={isMobile ? handleTouchStart : undefined}
                    onTouchEnd={isMobile ? handleTouchEnd : undefined}
                  >
                    {innerJsx}
                  </div>
                );
              })()}
            </DndContext>
          )}
        </div>
      </div>

      {/* Empty state: sede obbligatoria. Block any data-driven UI when no
          store is selected, so we never show stale/cross-store appointments. */}
      {!selectedStoreId && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/85 dark:bg-gray-900/85 backdrop-blur-sm">
          <div className="text-center max-w-sm px-6 py-8 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 shadow-sm">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
              <Store className="w-6 h-6 text-indigo-600 dark:text-indigo-300" />
            </div>
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Seleziona una sede
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Per pianificare gli appuntamenti devi prima scegliere la sede dal selettore in alto.
            </div>
            <button
              type="button"
              onClick={() => {
                const el =
                  document.querySelector<HTMLElement>('[data-store-selector]') ||
                  document.querySelector<HTMLElement>('[data-testid="store-selector"]');
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  const focusable = el.querySelector<HTMLElement>(
                    'button, [role="combobox"], select, input'
                  );
                  focusable?.focus();
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-indigo-600 hover:bg-indigo-500 text-white shadow-sm transition-colors min-h-[36px]"
            >
              <Store className="w-3.5 h-3.5" />
              Scegli sede
            </button>
          </div>
        </div>
      )}

      {/* Slot picker popover: choose a consultant for the empty slot just
          clicked when more than one resource is visible (admin convenience). */}
      {slotPicker && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setSlotPicker(null)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-label="Scegli consulente per il nuovo appuntamento"
            className="fixed z-50 w-64 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-2"
            style={{
              left: Math.min(window.innerWidth - 280, Math.max(8, slotPicker.x - 130)),
              top: Math.min(window.innerHeight - 320, slotPicker.y + 8),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-2 py-1.5 border-b border-gray-100 dark:border-gray-800 mb-1">
              <div className="text-[11px] font-semibold text-gray-900 dark:text-gray-100">
                Nuovo appuntamento
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">
                {fmtTime(slotPicker.start)}–{fmtTime(slotPicker.end)} · scegli consulente
              </div>
            </div>
            <div className="max-h-64 overflow-auto -mr-1 pr-1">
              {slotPicker.candidates.map((c) => {
                const accent = userAccentColor(c.user_id);
                return (
                  <button
                    key={c.user_id}
                    type="button"
                    disabled={!canManage || !canWriteCalendarObject(access, { assigned_to_id: c.user_id }, 'create')}
                    onClick={() => {
                      if (!canManage || !canWriteCalendarObject(access, { assigned_to_id: c.user_id }, 'create')) return;
                      const { start, end } = slotPicker;
                      setSlotPicker(null);
                      onCellClick(c.user_id, start, end, selectedServiceId || undefined);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs',
                      'hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors',
                      c.user_id === slotPicker.defaultUserId && 'bg-indigo-50 dark:bg-indigo-900/20',
                    )}
                    style={{ boxShadow: `inset 3px 0 0 0 ${accent}` }}
                  >
                    <UserAvatar
                      id={c.user_id}
                      name={c.name}
                      avatarUrl={c.avatar_url || undefined}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {c.name}
                      </div>
                      <div className={cn(
                        'text-[10px] truncate',
                        c.busy ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'
                      )}>
                        {c.busy ? 'Occupato in questo slot' : 'Libero'}
                      </div>
                    </div>
                    {c.busy ? (
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="border-t border-gray-100 dark:border-gray-800 mt-1 pt-1">
              <button
                type="button"
                onClick={() => setSlotPicker(null)}
                className="w-full text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 px-2 py-1 rounded transition-colors"
              >
                Annulla
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Counter({
  label,
  value,
  tone,
  pulse,
}: {
  label: string;
  value: number | string;
  tone: 'indigo' | 'amber' | 'emerald' | 'slate';
  pulse?: boolean;
}) {
  const toneClasses: Record<string, string> = {
    indigo: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    slate: 'bg-slate-50 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
  };
  const dotClasses: Record<string, string> = {
    indigo: 'bg-indigo-500',
    amber: 'bg-amber-500',
    emerald: 'bg-emerald-500',
    slate: 'bg-slate-500',
  };
  return (
    <div className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border', toneClasses[tone])}>
      {pulse ? (
        <span className="relative flex h-1.5 w-1.5">
          <span className={cn('animate-ping absolute inline-flex h-full w-full rounded-full opacity-75', dotClasses[tone])} />
          <span className={cn('relative inline-flex rounded-full h-1.5 w-1.5', dotClasses[tone])} />
        </span>
      ) : (
        <span className={cn('inline-flex w-1.5 h-1.5 rounded-full', dotClasses[tone])} />
      )}
      <span className="text-[10px] uppercase tracking-wide opacity-80">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function MiniCalendar({
  selectedDate,
  onSelect,
  heatmap,
  heatmapMax,
}: {
  selectedDate: Date;
  onSelect: (d: Date) => void;
  heatmap: Map<string, number>;
  heatmapMax: number;
}) {
  const first = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
  const startDow = (first.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);
  const weekDayLabels = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];
  const today = new Date();
  return (
    <div>
      <div className="grid grid-cols-7 gap-0.5 mb-0.5 text-[9px] text-gray-500 dark:text-gray-400 text-center">
        {weekDayLabels.map((l, i) => <div key={i}>{l}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const k = toLocalDateStr(d);
          const cnt = heatmap.get(k) || 0;
          const intensity = cnt === 0 ? 0 : Math.min(4, Math.ceil((cnt / heatmapMax) * 4));
          const heatBg = ['bg-transparent', 'bg-indigo-100 dark:bg-indigo-900/30', 'bg-indigo-300 dark:bg-indigo-700', 'bg-indigo-500 text-white', 'bg-indigo-700 text-white'][intensity];
          const isSelected = sameDay(d, selectedDate);
          const isToday = sameDay(d, today);
          return (
            <button
              key={i}
              onClick={() => onSelect(d)}
              className={cn(
                'aspect-square rounded text-[10px] flex items-center justify-center transition-colors relative',
                heatBg,
                isSelected && 'ring-2 ring-indigo-500',
                isToday && !isSelected && 'ring-1 ring-amber-400',
                'hover:opacity-80'
              )}
              title={`${d.toLocaleDateString('it-IT')} · ${cnt} appuntamenti`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="flex-1 p-4 animate-pulse">
      <div className="flex gap-2 mb-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 flex-1 rounded bg-gray-200 dark:bg-gray-800" />
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {Array.from({ length: 16 }).map((_, i) => (
          <div key={i} className="h-16 rounded bg-gray-200 dark:bg-gray-800" />
        ))}
      </div>
    </div>
  );
}
